import { Button, Loader, Snackbar } from 'components'

import { useNavigate } from 'react-router-dom'

import { LoginRequest, usePostLogon, usePostResetPassword } from 'services/auth'

import { FormItem, FormLabel } from 'components/Form'
import { useForm } from 'react-hook-form'

import { joiResolver } from '@hookform/resolvers/joi'
import { loginSchema } from 'domains/auth/screens/Login/schemas'
import { handleSetCookies } from 'services/auth/utilities/auth'

import styles from './Login.module.scss'
import { useContext, useEffect, useState } from 'react'
import { UserContext } from 'shared/contexts/user/user'
import Modal from 'components/ModalV2'
import { useTenant, useToast, useToggle } from 'shared/hooks'
import { TextField } from 'uikit'

export const Login: React.FunctionComponent = () => {
  const navigate = useNavigate()
  const { addToast } = useToast()
  const [welcomeImage, setWelcomeImage] = useState({ src: '', isLoaded: false })

  const [modals, setModals] = useState({
    forgotPassword: false,
    sendPassword: false,
  })

  const { tenant } = useTenant()
  const { setUser, fetchUserAccounts } = useContext(UserContext)

  const { mutate: mutateLogon, isLoading: isLoadingLogon } = usePostLogon()
  const { mutate: mutateResetPassWord } = usePostResetPassword()

  const passwordToast = useToggle()

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<LoginRequest>({
    mode: 'onChange',
    resolver: joiResolver(loginSchema),
  })

  const handleResetPassword = () => {
    mutateResetPassWord(
      { email: watch('login') },
      {
        onSuccess: () =>
          setModals({ sendPassword: true, forgotPassword: false }),
        onError: () => {
          addToast({message: 'Erro ao enviar email de recuperação de senha. Tente novamente.', type: 'alert'})
        }
      },
    )
  }

  const handleOnSubmit = (data: LoginRequest) => {
    mutateLogon(data, {
      onSuccess: (data) => {
        handleSetCookies({
          userId: data.id,
          name: data.name,
          email: watch('login'),
          profileImageURL: data.profileImageURL || '',
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        })

        localStorage.setItem('accountId', data.defaultAccountId || '')

        setUser({
          profileImageURL: data.profileImageURL || '',
          defaultAccountId: data.defaultAccountId || '',
          email: watch('login') || '',
          name: data.name,
        })

        navigate('/')

        fetchUserAccounts()
      },
      onError: (error) => {
        if (error?.response?.status === 401) {
          passwordToast.show()
        }
      },
    })
  }

  const loadImage = async () => {
    const loginImages = tenant?.ubiportal?.loginPage?.images

    if (loginImages?.length) {
      const randomIndex = Math.floor(Math.random() * loginImages.length - 1) + 1

      const url = loginImages[randomIndex] || ''

      setWelcomeImage({ src: url, isLoaded: false })
    }
  }

  const handleRender = () => {
    if (!welcomeImage) return 'loading'

    return 'view'
  }

  useEffect(() => {
    loadImage()
  }, [tenant])

  return (
    <div className={styles.main}>
      {
        {
          view: (
            <div className={styles.container}>
              {isLoadingLogon ||
                (!welcomeImage.isLoaded && (
                  <div className={styles.loader}>
                    <Loader appearance="screen" />
                  </div>
                ))}

              <Modal.Root
                isOpen={modals.forgotPassword}
                onClose={() =>
                  setModals((prev) => ({ ...prev, forgotPassword: false }))
                }
              >
                <Modal.Content className={styles.modalContent}>
                  <Modal.Title>Recuperar senha</Modal.Title>
                  <p className={styles.resetModalText}>
                    Informe o e-mail cadastrado na sua conta e lhe enviaremos um
                    link com as instruções para recuperar seu acesso ao portal
                    de monitoramento.
                  </p>

                  <FormItem>
                    <FormLabel>E-mail</FormLabel>
                    <TextField
                      {...register('login')}
                      onChange={(ev) =>
                        setValue('login', ev.currentTarget.value, {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                      message={errors?.login?.message}
                      appearance={errors?.login ? 'error' : 'neutral'}
                    />
                  </FormItem>
                  <Modal.Footer>
                    <Modal.Close asChild>
                      <Button appearance="tertiary">Cancelar</Button>
                    </Modal.Close>
                    <Button
                      width={87}
                      disabled={!!errors?.login}
                      onClick={handleResetPassword}
                      type="button"
                    >
                      Enviar link
                    </Button>
                  </Modal.Footer>
                </Modal.Content>
              </Modal.Root>

              <Modal.Root
                isOpen={modals.sendPassword}
                onClose={() =>
                  setModals((prev) => ({ ...prev, sendPassword: false }))
                }
              >
                <Modal.Content className={styles.modalContent}>
                  <Modal.Title>Senha solicitada com sucesso</Modal.Title>

                  <p>
                    Verifique o e-mail informado e siga as instruções para
                    recuperar o seu acesso ao Ubiportal
                  </p>

                  <Modal.Footer>
                    <Modal.Close asChild>
                      <Button appearance="tertiary">Cancelar</Button>
                    </Modal.Close>
                  </Modal.Footer>
                </Modal.Content>
              </Modal.Root>

              <div className={styles.assets}>
                <img
                  src={welcomeImage.src}
                  alt="Bem vindo!"
                  loading="eager"
                  onLoad={() =>
                    setWelcomeImage((prev) => ({ ...prev, isLoaded: true }))
                  }
                />
              </div>

              {welcomeImage.isLoaded && (
                <div className={styles.formContainer}>
                  <form onSubmit={handleSubmit(handleOnSubmit)}>
                    <div className={styles.wrapper}>
                      <div>
                        <h1>{tenant?.ubiportal.loginPage.text}</h1>
                        <h2>{tenant?.ubiportal.loginPage.greetingText}</h2>
                      </div>

                      <div className={styles.fields}>
                        {passwordToast.isVisible && (
                          <Snackbar
                            message="Usuário ou senha inválidos. Tente novamente ou clique em ‘Esqueci minha senha’."
                            error
                            autohideTimeout={0}
                            isVisible
                          />
                        )}

                        <TextField
                          label="Email"
                          {...register('login')}
                          spellCheck="false"
                          message={errors?.login?.message}
                          appearance={errors?.login ? 'error' : 'neutral'}
                        />

                        <TextField
                          label="Senha"
                          type="password"
                          {...register('password')}
                        />
                      </div>

                      <div className={styles.footer}>
                        <Button
                          style={{ width: '100%' }}
                          type="submit"
                          appearance="primary"
                        >
                          Entrar
                        </Button>

                        <Button
                          onClick={() =>
                            setModals((prev) => ({
                              ...prev,
                              forgotPassword: true,
                            }))
                          }
                          type="button"
                          appearance="tertiary"
                        >
                          Esqueci minha senha
                        </Button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>
          ),
          loading: <Loader appearance="screen" />,
        }[handleRender()]
      }
    </div>
  )
}
